import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, createSearchParams } from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { deleteRequest, getRequest, postRequest } from '../../../services/base'
import {
  DefPluginContext,
  SET_DEF_PLUGIN_DATA,
  SET_SELECTED_IDS,
  SET_UPLOAD_GENERATE_FILE_STATUS,
  SHOW_DEF_BULK_UPLOAD,
  SHOW_DEF_UPLOAD_POPUP,
} from './context/context'
import { getSessionStorageData } from '../../../utils/SessionStorage'
import { Dropdown, Empty, Flex, Form, Modal, Segmented, Space, Table, Tooltip, Typography } from 'antd'
import { DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined, EyeInvisibleOutlined, EyeOutlined, FileExcelOutlined, LoadingOutlined, MoreOutlined, ThunderboltOutlined, UploadOutlined } from '@ant-design/icons'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import RecordsCount from '../../common/RecordsCount'
import DefFormModal from './form'
import DefBulkUpload from './bulk_upload'
import { buildPageUrl, downloadFile, formatDateTime, formatEmptyValue, getDeleteTargetPage } from '../../../utils/common'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import { ProgresSpinner } from '../../common/ProgresSpinner'
import { FILE_CONFIG } from './constants'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const DefPlugin = () => {
  const { vehicle } = useParams()
  const navigate = useNavigate()
  const intl = useIntl()
  const [modal, contextHolder] = Modal.useModal()
  const [defPluginState, defPluginDispatch] = useContext(DefPluginContext)
  const [, dispatch] = useContext(NavContext)
  const [pagination, setPagination] = useState({})
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [expandedDetails, setExpandedDetails] = useState({})
  const intervalRef = useRef(null)
  const dataIntervalRef = useRef(null)
  const expandIntervalRef = useRef(null)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const currentUrl = useRef(`/api/plugin/def/?vehicle=${vehicle}&search=${searchValue}`)

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${vehicle} / ${intl.formatMessage({ id: 'sidebar.plugin' })} / ${intl.formatMessage({ id: 'sidebar.defPlugin' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })

    getDefPluginList(null, true)
    fetchDefStatusInterval()

    intervalRef.current = setInterval(fetchDefStatusInterval, 10000)
    dataIntervalRef.current = setInterval(() => getDefPluginList(), 30000)

    return () => {
      clearInterval(intervalRef.current)
      clearInterval(dataIntervalRef.current)
      clearInterval(expandIntervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (expandedRowKeys.length === 0) {
      clearInterval(expandIntervalRef.current)
      return
    }
    const id = expandedRowKeys[0]
    expandIntervalRef.current = setInterval(async () => {
      const res = await getRequest(`/api/plugin/def/${id}/?vehicle=${vehicle}`)
      setExpandedDetails((prev) => ({ ...prev, [id]: res }))
    }, 5000)
    return () => clearInterval(expandIntervalRef.current)
  }, [expandedRowKeys])

  const fetchDefStatusInterval = async () => {
    let res = await getRequest(`/api/plugin/def/upload_status_check/${vehicle}/?vehicle=${vehicle}`)
    if (res?.status) {
      defPluginDispatch({ type: SET_UPLOAD_GENERATE_FILE_STATUS, data: { ...res.data } })
    }
  }

  const getDefPluginList = async (url = null, showLoader = false) => {
    if (showLoader) setLoading(true)

    let response = await getRequest(currentUrl.current)
    defPluginDispatch({ type: SET_DEF_PLUGIN_DATA, data: response.results })
    if (response?.status === false) {
      notifyError(dispatch, intl.formatMessage({ id: 'defPlugin.loadError' }))
    }
    setPagination(response.pagination)
    if (showLoader) setLoading(false)
  }

  const downloadDefFiles = async (download_option = null) => {
    const downloadOptionLabel =
      download_option === 'All'
        ? intl.formatMessage({ id: 'generic.all' })
        : intl.formatMessage({ id: 'forms.selected' })

    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'defPlugin.downloadConfirmationTitle' }),
      content: intl.formatMessage(
        { id: 'defPlugin.downloadConfirmationContent' },
        { downloadOption: downloadOptionLabel }
      ),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.download' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        dispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'defPlugin.downloading' }), showLoader: true },
          notification: {},
        })

        let formData = new FormData()
        if (download_option === 'All') {
          formData.append('def_files', 'All')
        } else {
          defPluginState.selectedIds.forEach((element) => {
            formData.append('def_files', element)
          })
        }
        formData.append('vehicle', vehicle)

        let res = await postRequest(`/api/plugin/def/bulk/download/`, formData, true)
        // Was `if (res?.msg)`: a failure carrying no message fell through and
        // saved the error object as the file. Same guard as the other exports.
        if (res?.status === false || !(res instanceof Blob)) {
          dispatch({
            type: 'notification',
            data: { notification_type: 'error', msg: intl.formatMessage({ id: 'defPlugin.downloadError' }), loader: true },
          })
        } else {
          const url = window.URL.createObjectURL(new Blob([res]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${vehicle}_def.zip`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          dispatch({
            type: 'notification',
            data: { notification_type: 'success', msg: intl.formatMessage({ id: 'defPlugin.fileDownloaded' }), loader: true },
          })
        }
      },
    })
  }

  const generateBulkFiles = async (option_value) => {
    dispatch({
      type: 'loader',
      data: { caption: intl.formatMessage({ id: 'defPlugin.generating' }), showLoader: true },
      notification: {},
    })

    let formData = new FormData()
    if (option_value === 'generate_all_files') {
      formData.append('generate_files', 'all')
    } else {
      defPluginState.selectedIds.forEach((element) => {
        formData.append('generate_files', element)
      })
    }

    let res = await postRequest(`/api/plugin/def/bulk_generate_files/${vehicle}/`, formData)
    dispatch({ type: 'loader', data: {}, notification: {} })
    if (res?.status) {
      dispatch({
        type: 'notification',
        data: { notification_type: 'success', msg: intl.formatMessage({ id: 'defPlugin.generateStarted' }) },
      })
    } else {
      dispatch({
        type: 'notification',
        data: { notification_type: 'error', msg: intl.formatMessage({ id: 'defPlugin.generateError' }) },
      })
    }
  }

  const generateDefFiles = async (def_file_id) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'defPlugin.generateConfirmationTitle' }),
      content: intl.formatMessage({ id: 'defPlugin.generateConfirmationContent' }),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'defPlugin.generateFiles' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        dispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'defPlugin.generating' }), showLoader: true },
          notification: {},
        })

        let formData = new FormData()
        FILE_CONFIG.forEach((value) => {
          formData.append('file_types', value.key)
        })
        const res = await postRequest(`/api/plugin/def/generate_files/${def_file_id}/`, formData)
        dispatch({ type: 'loader', data: {}, notification: {} })
        // After the loader dispatch: a 'loader' action overwrites `notification`,
        // so notifying before it would be wiped out in the same React batch.
        if (res?.status === false) {
          notifyError(dispatch, intl.formatMessage({ id: 'defPlugin.generateError' }))
          return
        }
        fetchDefStatusInterval()
        if (expandedRowKeys.length > 0) {
          const id = expandedRowKeys[0]
          const res = await getRequest(`/api/plugin/def/${id}/?vehicle=${vehicle}`)
          setExpandedDetails((prev) => ({ ...prev, [id]: res }))
        }
      },
    })
  }

  const deleteDefFiles = async (def_file_id) => {
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'defPlugin.deleteConfirmationTitle' }),
      content: intl.formatMessage({ id: 'defPlugin.deleteConfirmationContent' }),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'forms.delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        const res = await deleteRequest(`/api/plugin/def/${def_file_id}/?vehicle=${vehicle}`)
        // The result used to be discarded, so the success toast below fired even
        // when the delete failed. base.js returns {status: false}, never rejects.
        if (res?.status === false) {
          notifyError(dispatch, intl.formatMessage({ id: 'defPlugin.deleteError' }))
          return
        }
        currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
        getDefPluginList(null, true)
        dispatch({
          type: 'notification',
          data: { notification_type: 'success', msg: intl.formatMessage({ id: 'defPlugin.deleted' }) },
        })
      },
    })
  }

  const toggleExpand = async (record) => {
    const id = record.id
    if (expandedRowKeys.includes(id)) {
      setExpandedRowKeys([])
      return
    }
    setExpandedRowKeys([id])
    const res = await getRequest(`/api/plugin/def/${id}/?vehicle=${vehicle}`)
    setExpandedDetails((prev) => ({ ...prev, [id]: res }))
  }

  const downloadDefFile = async (record) => {
    const saved = await downloadFile(
      record?.def_file && `${record.def_file}?timestamp=${Number(new Date())}`,
      record.file_names?.def_file
    )
    if (!saved) {
      notifyError(dispatch, intl.formatMessage({ id: 'defPlugin.downloadError' }))
    }
  }

  const locationPath = `/vehicles/${vehicle}/plugins/def`
  const menuItems = useMemo(() => [
    { label: intl.formatMessage({ id: 'sidebar.salesCodeComplexity' }), value: `/vehicles/${vehicle}/plugins/sales_code_complexity` },
    { label: intl.formatMessage({ id: 'sidebar.defPlugin' }), value: `/vehicles/${vehicle}/plugins/def` },
    { label: intl.formatMessage({ id: 'sidebar.capitalHarness' }), value: `/vehicles/${vehicle}/plugins/capital_harness` },
  ], [vehicle, intl])

  const dataSource = useMemo(() => (defPluginState?.data || []).map((item, index) => ({
    key: item.id || index,
    id: item.id,
    file_id: item.file_id,
    description: item.description,
    created_at: item.created_at,
    file_names: item.file_names,
    status: item.status,
    def_file: item.def_file,
    supp_file: item.supp_file,
    ...FILE_CONFIG.reduce((acc, row) => ({ ...acc, [row.key]: item[row.key] }), {}),
  })), [defPluginState?.data])

  const columns = useMemo(() => [
    {
      title: intl.formatMessage({ id: 'defPlugin.harnessId' }),
      dataIndex: 'harness_id',
      sorter: (a, b) => ((a.file_id || a.file_names?.def_file || '')).localeCompare((b.file_id || b.file_names?.def_file || '')),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <EllipsisTooltip className='ellipsis'>{formatEmptyValue(record.file_id || record.file_names?.def_file)}</EllipsisTooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: 'defPlugin.description' }),
      dataIndex: 'description',
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
      ellipsis: { showTitle: false },
      render: (text) => <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'generic.date' }),
      dataIndex: 'created_at',
      sorter: (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
      defaultSortOrder: 'descend',
      render: (text) => <EllipsisTooltip className='ellipsis'>{formatDateTime(text)}</EllipsisTooltip>,
    },
    {
      title: '',
      align: 'right',
      width: 510,
      render: (_, record) => {
        const isGenerating = (defPluginState?.in_process_generate_file_ids || []).includes(record.id)
        const isUploading = (defPluginState?.in_process_upload_file_harnesses || []).includes(record.file_id)
        if (isGenerating) {
          return <ProgresSpinner caption={intl.formatMessage({ id: 'defPlugin.fileGenerationInProgress' })} className="upload-inprogress" size="medium" />
        }
        if (isUploading) {
          return <ProgresSpinner caption={intl.formatMessage({ id: 'defPlugin.fileUploadInProgress' })} className="upload-inprogress" size="medium" />
        }
        return (
          <Space size="small">
            <Tooltip title={
              intl.formatMessage({ id: expandedRowKeys.includes(record.id) ? 'defPlugin.hideFiles' : 'defPlugin.viewFiles' })
            }>
              <div>
                <Button
                  type="link"
                  icon={expandedRowKeys.includes(record.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  className={`${expandedRowKeys.includes(record.id) ? 'link-button neutral' : 'link-button'}`}
                  onClick={() => toggleExpand(record)}
                >
                    {intl.formatMessage({ id: 'forms.files' })}
                </Button>
              </div>
            </Tooltip>

            <Tooltip title={ intl.formatMessage({ id: 'defPlugin.downloadDefFile' }) }>
              <div>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  className="link-button"
                  disabled={!record.def_file}
                  onClick={() => downloadDefFile(record)}
                >
                    {intl.formatMessage({ id: 'defPlugin.defFile' })}
                </Button>
              </div>
            </Tooltip>

            {record.supp_file && (
              <Tooltip title={ intl.formatMessage({ id: 'defPlugin.downloadSuppFile' }) }>
                <div>
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    className="link-button"
                    onClick={async () => {
                      const saved = await downloadFile(
                        `${record.supp_file}?timestamp=${Number(new Date())}`,
                        record.file_names?.supp_file
                      )
                      if (!saved) {
                        notifyError(dispatch, intl.formatMessage({ id: 'defPlugin.downloadError' }))
                      }
                    }}
                  >
                      {intl.formatMessage({ id: 'defPlugin.suppFile' })}
                  </Button>
                </div>
              </Tooltip>
            )}

            <Tooltip title={ intl.formatMessage({ id: 'defPlugin.generateFiles' }) }>
              {/* Note: The Antd Button needs to be wrapped in a <span> or <div> for the Tooltip to work */}
              <div>
                <Button
                  type="link"
                  icon={<ThunderboltOutlined />}
                  className="wrap-text link-button"
                  onClick={() => generateDefFiles(record.id)}
                >
                    {intl.formatMessage({ id: 'forms.files' })}
                </Button>
              </div>
            </Tooltip>

            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlayStyle={{ marginTop: 12 }}
              menu={{
                items: [
                  {
                    key: 'delete',
                    label: intl.formatMessage({ id: 'forms.delete' }),
                    icon: <DeleteOutlined style={{ color: 'var(--color-form-link-danger)' }} />,
                    danger: true,
                  },
                ],
                onClick: ({ key }) => {
                  if (key === 'delete') deleteDefFiles(record.id)
                },
              }}
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        )
      },
    },
  ], [defPluginState?.in_process_generate_file_ids, defPluginState?.in_process_upload_file_harnesses, expandedRowKeys, intl])

  const selectedRowKeys = defPluginState?.selectedIds || []

  const rowSelection = {
    selectedRowKeys,
    hideSelectAll: true,
    onSelect: (record, selected) => {
      defPluginDispatch({ type: SET_SELECTED_IDS, data: record.id })
    },
  }

  const downloadMenuItems = useMemo(() => [
    {
      key: 'all',
      label: intl.formatMessage({ id: 'defPlugin.downloadAllFiles' }),
      onClick: () => downloadDefFiles('All'),
    },
    {
      key: 'selected',
      label: intl.formatMessage({ id: 'defPlugin.downloadSelectedFiles' }),
      disabled: (defPluginState?.selectedIds?.length || 0) === 0,
      onClick: () => downloadDefFiles('selected'),
    },
  ], [defPluginState?.selectedIds, intl])

  const generateMenuItems = useMemo(() => [
    {
      key: 'generate_all_files',
      label: intl.formatMessage({ id: 'defPlugin.generateAllFiles' }),
      onClick: () => generateBulkFiles('generate_all_files'),
    },
    {
      key: 'generate_selected_files',
      label: intl.formatMessage({ id: 'defPlugin.generateSelectedFiles' }),
      disabled: (defPluginState?.selectedIds?.length || 0) === 0,
      onClick: () => generateBulkFiles('generate_selected_files'),
    },
  ], [defPluginState?.selectedIds, intl])

  const uploadMenuItems = useMemo(() => [
    {
      key: 'upload_new_file',
      label: intl.formatMessage({ id: 'defPlugin.uploadNewFile' }),
      onClick: () => defPluginDispatch({ type: SHOW_DEF_UPLOAD_POPUP, data: true }),
    },
    {
      key: 'upload_multiple_files',
      label: intl.formatMessage({ id: 'defPlugin.uploadMultipleFiles' }),
      onClick: () => defPluginDispatch({ type: SHOW_DEF_BULK_UPLOAD, data: true }),
    },
  ], [defPluginDispatch, intl])

  const { currentPage, totalCount } = pagination

  return (
    <div className="page def-plugin">
      <Flex className="title">
        <Segmented
          options={menuItems}
          value={locationPath}
          onChange={(value) => navigate(value)}
        />
      </Flex>

      <Flex vertical className="body">
        <Flex align="center" justify="space-between" gap={16} wrap>
          <Flex align="center" gap={8} style={{ flex: 1 }}>
            <Form style={{width: '100%'}}>
              <InputText
                name="search_value"
                placeholder={intl.formatMessage({ id: 'defPlugin.searchPlaceholder' })}
                value={searchValue}
                type="search"
                onChange={(value) => setSearchValue(value)}
                isRequired={false}
                wrapperClass="search-wrapper"
                onSubmit={() => {
                  currentUrl.current = `/api/plugin/def/?vehicle=${vehicle}&search=${searchValue}`
                  getDefPluginList('', true)
                }}
              />
            </Form>
          </Flex>

          <Space size="middle">
            {defPluginState?.in_process_upload_file_harnesses?.length > 0 && (
              <ProgresSpinner caption={intl.formatMessage({ id: 'defPlugin.fileUploadInProgress' })} className="upload-inprogress" size="medium" />
            )}

            <Dropdown menu={{ items: downloadMenuItems }} trigger={['click']}>
              <Button icon={<DownloadOutlined />}>
                {intl.formatMessage({ id: 'forms.download' })}
              </Button>
            </Dropdown>

            <Dropdown menu={{ items: generateMenuItems }} trigger={['click']}>
              <Button icon={<ThunderboltOutlined />}>
                {intl.formatMessage({ id: 'defPlugin.generateFiles' })}
              </Button>
            </Dropdown>

            <Dropdown menu={{ items: uploadMenuItems }} trigger={['click']}>
              <Button type="primary" icon={<UploadOutlined />}>
                {intl.formatMessage({ id: 'defPlugin.upload' })}
              </Button>
            </Dropdown>
          </Space>
        </Flex>

        <Table
          rowKey="key"
          className='def-table'
          tableLayout='fixed'
          scroll={dataSource?.length ? {x: 925} : undefined}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          showSorterTooltip={false}
          rowSelection={rowSelection}
          expandable={{
            expandIcon: () => null,
            showExpandColumn: false,
            expandedRowKeys,
            expandedRowRender: (record) => {
              const detail = expandedDetails[record.id] || record
              return (
                <div className="def-files-panel">
                  <div className='files-grid'>
                  {FILE_CONFIG.map((row, index) => {
                    const fileUrl = detail?.[row.key]
                    const status = fileUrl
                      ? 'generated'
                      : detail?.status === 1
                        ? 'not_generated'
                        : 'in_progress'
                    return (
                      <div key={`file-config-${index}`} className="file-item">
                        <div className="files-cell file-name">
                          <Flex align="center" gap={8}>
                            <FileExcelOutlined />
                            <Typography.Text>{intl.formatMessage({ id: row.id })}</Typography.Text>
                          </Flex>
                        </div>
                        
                        <div className="files-cell file-status">
                          {status === 'generated' ? (
                            <Button
                              type="link"
                              icon={<DownloadOutlined />}
                              className="link-button"
                              onClick={() => {
                                window.open(`${fileUrl}?timestamp=${Number(new Date())}`, '_blank')
                              }}
                            >
                              {intl.formatMessage({ id: 'forms.download' })}
                            </Button>
                          ) : status === 'not_generated' ? (
                            <Typography.Text className='status-msg not-generated'>
                              {intl.formatMessage({ id: 'defPlugin.fileNotGenerated' })}
                            </Typography.Text>
                          ) : (
                            <Typography.Text className='status-msg generation-in-progress'>
                              <LoadingOutlined />
                              {intl.formatMessage({ id: 'defPlugin.fileGenerationInProgress' })}
                            </Typography.Text>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  </div>
                </div>
              )
            },
            rowExpandable: () => true,
          }}
          pagination={{
            current: currentPage,
            total: totalCount,
            pageSize: 10,
            showSizeChanger: false,
            hideOnSinglePage: true,
            showTotal: (total) => <RecordsCount count={total} />,
            onChange: (page) => {
              const offset = (page - 1) * 10
              const url = pagination?.nextPageLink ? new URL(pagination.nextPageLink) : new URL(pagination?.prevPageLink)
              url.searchParams.set('offset', offset)
              currentUrl.current = url.toString()
              getDefPluginList('', true)
            },
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px' }}>
                <Empty description={<RecordsCount count={0} />} />
              </div>
            ),
          }}
        />
        {pagination &&
        pagination?.totalPages <= 1 &&
        pagination?.totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={pagination?.totalCount} />
          </Typography.Text>
        )}
      </Flex>

      <DefFormModal getDefPluginList={getDefPluginList} />
      <DefBulkUpload onClose={() => getDefPluginList('', true)} />
      {contextHolder}
    </div>
  )
}

export default DefPlugin
